import { useEffect, useState, type SubmitEventHandler } from "react";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { ApiClientError } from "../../lib/api/client";
import { useCreateReceiptMutation } from "../../lib/api/transactions";
import { formatMoney } from "../../lib/format/money";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { AllocationLinesEditor } from "./AllocationLinesEditor";
import {
  type AllocationLineForm,
  sumAllocationAmounts,
} from "./transaction-utils";

interface RecordIncomeModalProps {
  open: boolean;
  onClose: () => void;
  units: { id: string; name: string; code: string }[];
  funds: { id: string; name: string }[];
  categories: { id: string; name: string }[];
  bankAccounts: { id: string; name: string }[];
}

export function RecordIncomeModal({
  open,
  onClose,
  units,
  funds,
  categories,
  bankAccounts,
}: RecordIncomeModalProps) {
  const createReceipt = useCreateReceiptMutation();
  const { defaultDate } = useFiscalDefaults();
  const [source, setSource] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [bankAccountId, setBankAccountId] = useState(bankAccounts[0]?.id ?? "");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<AllocationLineForm[]>([
    {
      unitId: units[0]?.id ?? "",
      fundId: funds[0]?.id ?? "",
      categoryId: categories[0]?.id ?? "",
      amount: "",
    },
  ]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && defaultDate) {
      setReceiptDate(defaultDate);
    }
  }, [open, defaultDate]);

  const total = sumAllocationAmounts(lines);

  async function submitReceipt() {
    setError(null);
    try {
      await createReceipt.mutateAsync({
        source,
        receiptDate,
        bankAccountId,
        note: note || undefined,
        lines: lines.map((line) => ({
          unitId: line.unitId,
          fundId: line.fundId,
          categoryId: line.categoryId,
          amount: Number.parseFloat(line.amount).toFixed(2),
        })),
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not post receipt.");
    }
  }

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    void submitReceipt();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record income"
      description="Record money actually received."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="record-income-form"
            disabled={createReceipt.isPending}
          >
            {createReceipt.isPending ? "Posting…" : "Post receipt"}
          </Button>
        </>
      }
    >
      <form id="record-income-form" className="grid gap-4" onSubmit={handleSubmit}>
        {error ? (
          <p className="edlab-error">
            {error}
          </p>
        ) : null}
        <label className="grid gap-1 text-sm">
          Received from
          <input
            className="edlab-input"
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
            }}
            required
          />
        </label>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-1 text-sm">
            Date received
            <input
              type="date"
              className="edlab-input"
              value={receiptDate}
              onChange={(e) => {
                setReceiptDate(e.target.value);
              }}
              required
            />
          </label>
          <label className="grid gap-1 text-sm">
            Bank / cash account
            <select
              className="edlab-input"
              value={bankAccountId}
              onChange={(e) => {
                setBankAccountId(e.target.value);
              }}
            >
              {bankAccounts.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="grid gap-1 text-sm">
          Description
          <input
            className="edlab-input"
            value={note}
            placeholder="What is this transaction for?"
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </label>
        <AllocationLinesEditor
          lines={lines}
          onChange={setLines}
          units={units}
          funds={funds}
          categories={categories}
        />
        <p className="text-sm font-medium text-edlab-ink">
          Total: {formatMoney(total.toFixed(2))}
        </p>
      </form>
    </Modal>
  );
}
